import React from "react";
import { connect } from "react-redux";
import { Col, Badge } from "react-bootstrap";
import PropTypes from "prop-types";

import Table from "../../../layout/table";
import ConfirmModal from "../../../layout/modal";
import { format as formatDate } from "../../../utils/date";

import { send as pageInfo, loading } from "../../../actions/page";
import { withNavigation } from "../../../utils/wrapper";
import { search as getPayroll, pay, unpay } from "../../../actions/pay.checks";
import { search as getTimecards, lock as lockTimecard, unlock as unlockTimecard, approve as approveTimecard, unapprove as unapproveTimecard } from "../../../actions/timecards";

import { initialState, onDateFormat, onCurrencyFormat, onLinkFormat } from "../../../layout/table/helpers";
import { groupTimecardsForPayroll } from "./helpers";

const BASE_URL = "/dashboard/accounting/payroll/";

class Payroll extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            checks: initialState({ size: 10, order: "endsOn" }),
            available: initialState({ size: 1000, order: "displayName" }),
            modal: {
                confirm: {
                    show: false,
                }
            }
        };
        this.props.loading(true);
    }

    componentDidMount = () => {
        setTimeout(() => {
            this.loadData();
        }, 30);
    };


    // -------------------------
    // Action handlers (STUBS)
    // -------------------------
    onLock = async (row) => {
        await this.props.lockTimecard({ uuids: [...row.timecardUuids], byUuid: this.props.auth.user.uuid });
        await this.loadData();
    };

    onUnlock = async (row) => {
        await this.props.unlockTimecard({ uuids: [...row.timecardUuids], byUuid: this.props.auth.user.uuid });
        await this.loadData();
    };

    onApprove = async (row) => {
        await this.props.approveTimecard({ uuids: [...row.timecardUuids], byUuid: this.props.auth.user.uuid });
        await this.loadData();
    };

    onUnapprove = async (row) => {
        await this.props.unapproveTimecard({ uuids: [...row.timecardUuids], byUuid: this.props.auth.user.uuid });
        await this.loadData();
    };

    onPay = async (row) => {
        await this.props.pay(row.staffUuid, row.payPeriodUuid);
        await this.loadData();
    };

    onUnpay = async (row) => {
        await this.props.unpay(row.payrollCheckUuid);
        await this.loadData();
    };
// -------------------------
// Badge click affordance + workflow renderer
// -------------------------
    onActionRender = (e, row) => {
        const lockedAt = row?.lockedAt ?? null;
        const approvedAt = row?.approvedAt ?? null;

        // "paid/issued/calculated/etc" presence is what matters for gating actions
        const hasCheck = !!row?.payrollCheckUuid;

        const asClickable = (onClick) => ({
            role: "button",
            tabIndex: 0,
            onClick,
            style: { cursor: "pointer", userSelect: "none" },
            onKeyDown: (ev) => {
                if (ev.key === "Enter" || ev.key === " ") {
                    ev.preventDefault();
                    onClick?.(ev);
                }
            },
        });

        const confirmUnpay = () => {
            this.setState(
                (prev) => ({
                    modal: {
                        ...prev.modal,
                        confirm: {
                            ...prev.modal.confirm,
                            show: true,
                            title: "Delete paid check?",
                            body:
                                "This will delete the payroll check and unlink the associated timecards. Continue?",
                            // your ConfirmModal button uses `this.state.modal.confirm.onOk`
                            onOk: async () => {
                                // close first (avoids double-click issues)
                                this.onConfirmHideModal();

                                await this.onUnpay(row);
                            },
                        },
                    },
                }),
                () => { }
            );
        };

        // 1) Not locked => can lock
        if (!lockedAt) {
            return (
                <Badge pill bg="warning" text="dark" {...asClickable(() => this.onLock(row))}>
                    Lock
                </Badge>
            );
        }

        // 2) Locked + approved
        if (approvedAt) {
            return (
                <>
                    <Badge pill bg="secondary">
                        Locked {formatDate(lockedAt)}
                    </Badge>{" "}

                    {/* ✅ rule: cannot unapprove if there has been a pay action (i.e., check exists) */}
                    {!hasCheck ? (
                        <Badge pill bg="success" {...asClickable(() => this.onUnapprove(row))}>
                            Approved {formatDate(approvedAt)} (revoke)
                        </Badge>
                    ) : (
                        <Badge pill bg="success">
                            Approved {formatDate(approvedAt)}
                        </Badge>
                    )}{" "}

                    {/* Pay / Paid */}
                    {!hasCheck ? (
                        <Badge pill bg="primary" {...asClickable(() => this.onPay(row))}>
                            Pay
                        </Badge>
                    ) : (
                        <Badge pill bg="primary" {...asClickable(() => confirmUnpay())}>
                            Paid (delete)
                        </Badge>
                    )}
                </>
            );
        }

        // 3) Locked + not approved
        return (
            <>
                <Badge pill bg="secondary" {...asClickable(() => this.onUnlock(row))}>
                    Locked {formatDate(lockedAt)} (revoke)
                </Badge>{" "}
                <Badge pill bg="success" {...asClickable(() => this.onApprove(row))}>
                    Approve
                </Badge>
            </>
        );
    };
    
    onConfirmHideModal = (e) => {
        this.setState(prev => ({
            modal: {
                ...prev.modal,
                confirm: {
                    ...prev.modal.confirm,
                    show: false
                }
            }
        }));
    }

    

    // -------------------------
    // Loading
    // -------------------------
    loadData = async () => {
        this.props.loading(true);

        const payroll = await this.props.getPayroll({ ...this.state.checks });
        let payroll_count = 0;
        if (payroll?.items?.length > 0) {
            payroll_count = Math.ceil(Number(payroll.count) / Number(this.state.checks.size));
        }

        const timecards = await this.props.getTimecards({ ...this.state.available });
        let timecards_count = 0;
        if (timecards?.items?.length > 0) {
            timecards_count = Math.ceil(Number(timecards.count) / Number(this.state.available.size));
        }

        const grouped = groupTimecardsForPayroll(timecards?.items || []);

        this.setState((prev) => ({
            checks: {
                ...prev.checks,
                data: payroll?.items || [],
                pages: payroll_count,
            },
            available: {
                ...prev.available,
                data: grouped,
                pages: timecards_count,
            },
        }));

        await this.props.pageInfo({ title: `Pay Checks`, breadcrumb: `Pay Checks` });
        this.props.loading(false);
    };

    // -------------------------
    // Headers as methods (so "this" is always the component)
    // -------------------------
    getChecksHeaders = () => [
        {
            header: "Check #",
            sortable: true,
            source: "checkNumber",
            className: "text-muted text-small text-uppercase w-15",
            onFormat: (e, row) =>
                onLinkFormat(e, row, {
                    baseUrl: BASE_URL,
                    id: row.uuid,
                    text: row.checkNumber ?? "[unassigned]",
                }),
        },
        {
            header: "Date",
            sortable: true,
            source: "payDate",
            className: "text-muted text-small text-uppercase w-15",
            onRender: (e, row) => onDateFormat(e, row),
        },
        {
            header: "Payee",
            sortable: true,
            source: "payeeName",
            className: "text-muted text-small text-uppercase w-30",
            onFormat: (e, row) => onLinkFormat(e, row, { baseUrl: BASE_URL, id: row.uuid, text: row.fullName }),
        },
        {
            header: "Gross",
            sortable: true,
            source: "grossPay",
            className: "text-muted text-small text-uppercase w-20",
            onRender: (e, row) => onCurrencyFormat(e, row),
        },
        {
            header: "Net",
            sortable: true,
            source: "netPay",
            className: "text-muted text-small text-uppercase w-20",
            onRender: (e, row) => onCurrencyFormat(e, row),
        },
    ];

    getAvailableHeaders = () => [
        {
            header: "Employee",
            sortable: true,
            source: "displayName",
            className: "text-muted text-small text-uppercase w-15",
        },
        {
            header: "Times",
            sortable: false,
            source: "segments",
            className: "text-muted text-small text-uppercase w-35",
        },
        {
            header: "Total Time",
            sortable: false,
            source: "totalHours",
            className: "text-muted text-small text-uppercase w-10",
        },
        {
            header: "Gross",
            sortable: true,
            source: "grossPay",
            className: "text-muted text-small text-uppercase w-10",
        },
        {
            header: "Net",
            sortable: true,
            source: "netPay",
            className: "text-muted text-small text-uppercase w-10",
        },
        {
            header: "",
            sortable: true,
            source: "uuid",
            className: "text-muted text-small text-uppercase w-20",
            onRender: (e, row) => this.onActionRender(e, row),
        },
    ];

    // -------------------------
    // Table controls (fixed: use prefix state)
    // -------------------------
    onColumnClick = (e, prefix) => {
        const headers = this.getHeadersByPrefix(prefix);
        const idx = headers.findIndex((item) => item.header === e.header && item.source === e.source);

        this.setState(
            (prev) => ({
                [prefix]: {
                    ...prev?.[prefix],
                    index: idx,
                    direction: prev?.[prefix]?.index === idx && prev?.[prefix]?.direction === "DESC" ? "ASC" : "DESC",
                    order: headers?.[idx]?.source,
                    page: 0,
                },
            }),
            this.loadData
        );
    };

    getHeadersByPrefix = (prefix) => {
        if (prefix === "checks") return this.getChecksHeaders();
        if (prefix === "available") return this.getAvailableHeaders();
        return [];
    };

    onSearch = (e, prefix) => {
        this.setState(
            (prev) => ({
                [prefix]: {
                    ...prev?.[prefix],
                    search: e,
                    page: 0,
                },
            }),
            this.loadData
        );
    };

    onSize = (e, prefix) => {
        this.setState(
            (prev) => ({
                [prefix]: {
                    ...prev?.[prefix],
                    size: Number(e),
                    page: 0,
                },
            }),
            this.loadData
        );
    };

    onPageChange = (e, prefix) => {
        this.setState(
            (prev) => ({
                [prefix]: {
                    ...prev?.[prefix],
                    page: e,
                },
            }),
            this.loadData
        );
    };

    render() {
        return (
            <>
                <Col xs="12">
                    <Table
                        headers={this.getChecksHeaders()}
                        page={this.state.checks.data}
                        sort={{ index: this.state.checks.index, direction: this.state.checks.direction }}
                        size={this.state.checks.size}
                        index={this.state.checks.page}
                        pages={this.state.checks.pages}
                        onColumnClick={(e) => this.onColumnClick(e, "checks")}
                        onPageChange={(e) => this.onPageChange(e, "checks")}
                        onSearch={(e) => this.onSearch(e, "checks")}
                        onSize={(e) => this.onSize(e, "checks")}
                        searchable
                        sizable
                    />
                </Col>

                <Col xs="12">
                    <Table
                        headers={this.getAvailableHeaders()}
                        page={this.state.available.data}
                        sort={{ index: this.state.available.index, direction: this.state.available.direction }}
                        size={this.state.available.size}
                        index={this.state.available.page}
                        pages={this.state.available.pages}
                        onColumnClick={(e) => this.onColumnClick(e, "available")}
                        onPageChange={(e) => this.onPageChange(e, "available")}
                        onSearch={(e) => this.onSearch(e, "available")}
                        onSize={(e) => this.onSize(e, "available")}
                        searchable={false}
                        sizable={false}
                    />
                </Col>

                <ConfirmModal {...this.state.modal.confirm} buttons={
                    [
                        {
                            caption: "ok",
                            onEnabledClick: this.state.modal.confirm.onOk,
                            variant: "secondary",
                            enabled: true,
                            display: true
                        },
                        {
                            default: true,
                            caption: "cancel",
                            onEnabledClick: this.onConfirmHideModal,
                            variant: "primary",
                            enabled: true,
                            display: true
                        }
                    ]
                }></ConfirmModal>
            </>
        );
    }
}

Payroll.propTypes = {
  auth: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  auth: state.auth,
});

export default withNavigation(connect(mapStateToProps, { loading, pageInfo, getPayroll, getTimecards, lockTimecard, unlockTimecard, approveTimecard, unapproveTimecard, pay, unpay })(Payroll));
